// src/utils/rutValidator.js

/**
 * Valida un RUT chileno usando el algoritmo del módulo 11
 */
export const validarRutChileno = (rut, dv) => {
  try {
    const rutLimpio = rut.toString().replace(/\D/g, '');
    const dvUpperCase = dv.toString().toUpperCase();
    
    if (!/^[0-9K]$/.test(dvUpperCase)) {
      return false;
    }
    
    if (rutLimpio.length < 7 || rutLimpio.length > 8) {
      return false;
    }
    
    const rutInvertido = rutLimpio.split('').reverse();
    const factores = [2, 3, 4, 5, 6, 7];
    let suma = 0;
    
    for (let i = 0; i < rutInvertido.length; i++) {
      suma += parseInt(rutInvertido[i]) * factores[i % factores.length];
    }
    
    const resto = suma % 11;
    const dvCalculado = 11 - resto;
    
    let dvEsperado;
    if (dvCalculado === 11) {
      dvEsperado = '0';
    } else if (dvCalculado === 10) {
      dvEsperado = 'K';
    } else {
      dvEsperado = dvCalculado.toString();
    }
    
    return dvUpperCase === dvEsperado;
    
  } catch (error) {
    console.error('Error validando RUT:', error);
    return false;
  }
};

/**
 * Formatea un RUT agregando puntos y guión
 */
export const formatearRut = (rut) => {
  if (!rut) return '';
  
  const rutLimpio = rut.toString().replace(/[^0-9K]/gi, '').toUpperCase();
  
  if (rutLimpio.length < 2) return rutLimpio;
  
  const cuerpo = rutLimpio.slice(0, -1);
  const dv = rutLimpio.slice(-1);
  
  const cuerpoFormateado = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${cuerpoFormateado}-${dv}`;
};

/**
 * Separar RUT para envío al backend
 */
export const limpiarRut = (rutCompleto) => {
  if (!rutCompleto) return { rut: '', dv: '' };
  
  const rutLimpio = rutCompleto.toString().replace(/[^0-9K]/gi, '').toUpperCase();
  
  if (rutLimpio.length < 2) {
    return { rut: rutLimpio, dv: '' };
  }
  
  return {
    rut: rutLimpio.slice(0, -1),
    dv: rutLimpio.slice(-1)
  };
};

/**
 * Validar RUT completo
 */
export const validarRutCompleto = (rutCompleto) => {
  const { rut, dv } = limpiarRut(rutCompleto);
  
  if (!rut || !dv) return false;
  
  return validarRutChileno(rut, dv);
};

/**
 * FUNCIÓN PRINCIPAL - Procesa RUT para enviar al backend
 */
export const procesarRutParaBackend = (rutInput) => {
  const { rut, dv } = limpiarRut(rutInput);
  const esValido = rut && dv ? validarRutChileno(rut, dv) : false;
  
  return {
    rut_numero: rut || '',
    dv_emisor: dv || '',
    rut_completo: rutInput,
    rut_formateado: formatearRut(rutInput),
    es_valido: esValido,
    para_backend: {
      rut_emisor: rut || '',
      dv_emisor: dv || ''
    }
  };
};

/**
 * Hook para validación de RUT en tiempo real
 */
export const useValidacionRut = (valor) => {
  if (!valor) {
    return {
      esValido: null,
      mensaje: '',
      rutFormateado: ''
    };
  }
  
  const { rut, dv } = limpiarRut(valor);
  
  if (!dv) {
    return {
      esValido: null,
      mensaje: 'Ingrese el dígito verificador',
      rutFormateado: formatearRut(valor)
    };
  }
  
  const esValido = validarRutChileno(rut, dv);
  const datosCompletos = procesarRutParaBackend(valor);
  
  return {
    esValido,
    mensaje: esValido ? 'RUT válido' : 'RUT inválido',
    rutFormateado: formatearRut(valor),
    ...datosCompletos
  };
};

/**
 * Generar clave única para validación de duplicados
 */
export const generarClaveDocumento = (tipoDocumentoId, numero, rutEmisor) => {
  return `${tipoDocumentoId}-${numero}-${rutEmisor}`.toUpperCase();
};

/**
 * Validar duplicados en array local
 */
export const validarDuplicadoLocal = (items, nuevoItem, excludeIndex = -1) => {
  if (!nuevoItem.tipo_documento_id || !nuevoItem.numero || !nuevoItem.rut_emisor) {
    return false;
  }
  
  const claveNueva = generarClaveDocumento(
    nuevoItem.tipo_documento_id,
    nuevoItem.numero,
    nuevoItem.rut_emisor
  );
  
  return items.some((item, index) => {
    if (index === excludeIndex) return false;
    
    if (!item.tipo_documento_id || !item.numero || !item.rut_emisor) {
      return false;
    }
    
    const claveExistente = generarClaveDocumento(
      item.tipo_documento_id,
      item.numero,
      item.rut_emisor
    );
    
    return claveExistente === claveNueva;
  });
};

/**
 * Generar mensaje de error para duplicados
 */
export const generarMensajeDuplicado = (numero, rutEmisor, razonSocial = '') => {
  const emisor = razonSocial ? `${razonSocial} (${rutEmisor})` : rutEmisor;
  return `Documento duplicado: N° ${numero} del emisor ${emisor}`;
};

// Constantes
export const RUT_PATTERNS = {
  SOLO_NUMEROS: /^\d+$/,
  RUT_COMPLETO: /^\d{1,8}-[0-9K]$/i,
  RUT_CON_PUNTOS: /^\d{1,3}(\.\d{3})*-[0-9K]$/i
};

export const RUT_CONFIG = {
  FORMATO_BACKEND: {
    rut_emisor: 'string',
    dv_emisor: 'string'
  },
  VALIDAR_EN_FRONTEND: true,
  BACKEND_SOLO_PROCESA: true
};

export const DUPLICADOS_CONFIG = {
  CLAVE_DOCUMENTO: 'tipo_documento_id + numero + rut_emisor',
  VALIDAR_FRONTEND: true,
  VALIDAR_BACKEND: true
};
